/**
 * The playground's request, and the code examples describing it.
 *
 * One state shape produces both. That is the whole point: a snippet built from anything other than
 * the exact query the playground just sent would eventually describe a different request, and the
 * customer would copy something that does not reproduce what they saw.
 *
 * The API key is never inlined into a snippet. Every example reads it from an environment
 * variable, because a copyable block containing a live credential is a block that ends up in a
 * commit, a chat message, or a screenshot.
 */
import { DEFAULT_WAIT_UNTIL, type WaitUntil } from '@urlshot/api-contract';

/** Playground form state, in the same vocabulary as the public query parameters. */
export interface PlaygroundOptions {
  url: string;
  format: 'png' | 'jpeg' | 'webp';
  viewportWidth: number;
  viewportHeight: number;
  deviceScaleFactor: number;
  fullPage: boolean;
  /** One element to capture instead of the viewport. Empty for none. */
  selector: string;
  quality: number | null;
  waitUntil: WaitUntil;
  delayMs: number;
  timeoutMs: number;
  darkMode: boolean;
  hideSelectors: string[];
  blockCookieBanners: boolean;
  cacheTtl: number;
}

export const PLAYGROUND_DEFAULTS: PlaygroundOptions = {
  url: 'https://example.com',
  format: 'png',
  viewportWidth: 1280,
  viewportHeight: 720,
  deviceScaleFactor: 1,
  fullPage: false,
  selector: '',
  quality: null,
  waitUntil: DEFAULT_WAIT_UNTIL,
  delayMs: 0,
  timeoutMs: 15_000,
  darkMode: false,
  hideSelectors: [],
  blockCookieBanners: false,
  cacheTtl: 0,
};

/**
 * Build the query string.
 *
 * Only values that differ from the API's own defaults are emitted, so the generated examples stay
 * short and show what the customer actually chose. `quality` is sent only for the lossy formats,
 * matching the contract, which rejects it for png, and `full_page` only without a `selector`, which
 * the contract refuses to combine with it.
 */
export function buildQuery(options: PlaygroundOptions): URLSearchParams {
  const params = new URLSearchParams();
  params.set('url', options.url);

  if (options.format !== 'png') params.set('format', options.format);
  if (options.viewportWidth !== 1280) params.set('viewport_width', String(options.viewportWidth));
  if (options.viewportHeight !== 720) params.set('viewport_height', String(options.viewportHeight));
  if (options.deviceScaleFactor !== 1) {
    params.set('device_scale_factor', String(options.deviceScaleFactor));
  }
  // A selector wins, as the form shows it: the Full page checkbox is hidden while one is entered,
  // and a checkbox left ticked underneath must not turn the request into an invalid one.
  const selector = options.selector.trim();
  if (options.fullPage && selector === '') params.set('full_page', 'true');
  if (selector !== '') params.set('selector', selector);
  if (options.quality !== null && options.format !== 'png') {
    params.set('quality', String(options.quality));
  }
  // From the contract, not spelled out: when the default moved from `load` to `auto`, a literal
  // here would have sent `wait_until=auto` on every request and dropped an explicit `load`.
  if (options.waitUntil !== DEFAULT_WAIT_UNTIL) params.set('wait_until', options.waitUntil);
  if (options.delayMs > 0) params.set('delay_ms', String(options.delayMs));
  if (options.timeoutMs !== 15_000) params.set('timeout_ms', String(options.timeoutMs));
  if (options.darkMode) params.set('dark_mode', 'true');
  // Repeatable, so each selector is its own parameter rather than a joined string.
  for (const selector of options.hideSelectors) {
    if (selector.trim() !== '') params.append('hide_selectors', selector.trim());
  }
  if (options.blockCookieBanners) params.set('block_cookie_banners', 'true');
  if (options.cacheTtl > 0) params.set('cache_ttl', String(options.cacheTtl));

  return params;
}

/** The full request URL the playground sends and every snippet reproduces. */
export function buildRequestUrl(gatewayOrigin: string, options: PlaygroundOptions): string {
  return `${gatewayOrigin}/v1/screenshot?${buildQuery(options).toString()}`;
}

export type SnippetLanguage = 'curl' | 'node' | 'php' | 'python';

export const SNIPPET_LANGUAGES: { id: SnippetLanguage; label: string }[] = [
  { id: 'curl', label: 'cURL' },
  { id: 'node', label: 'Node.js' },
  { id: 'php', label: 'PHP' },
  { id: 'python', label: 'Python' },
];

/** Environment variable every example reads the key from, rather than embedding it. */
const KEY_VARIABLE = 'URLSHOT_API_KEY';

/**
 * Single-quote a value for a shell.
 *
 * A target URL can legitimately contain characters a shell would interpret, and an unquoted
 * example that works for `example.com` and breaks for a real URL is worse than no example.
 */
function shellQuote(value: string): string {
  return `'${value.replaceAll("'", `'\\''`)}'`;
}

export function generateSnippet(
  language: SnippetLanguage,
  gatewayOrigin: string,
  options: PlaygroundOptions,
): string {
  const url = buildRequestUrl(gatewayOrigin, options);
  const extension = options.format;

  switch (language) {
    case 'curl':
      return [
        `curl --fail --silent --show-error \\`,
        `  --header "Authorization: Bearer $${KEY_VARIABLE}" \\`,
        `  --output screenshot.${extension} \\`,
        `  ${shellQuote(url)}`,
      ].join('\n');

    case 'node':
      return [
        `import { writeFile } from 'node:fs/promises';`,
        ``,
        `const response = await fetch(${JSON.stringify(url)}, {`,
        `  headers: { authorization: \`Bearer \${process.env.${KEY_VARIABLE}}\` },`,
        `});`,
        ``,
        `if (!response.ok) {`,
        `  // The error body is the documented JSON envelope, with a request id worth logging.`,
        `  throw new Error(JSON.stringify(await response.json()));`,
        `}`,
        ``,
        `await writeFile('screenshot.${extension}', Buffer.from(await response.arrayBuffer()));`,
        `console.log('request id:', response.headers.get('x-request-id'));`,
      ].join('\n');

    case 'php':
      return [
        `<?php`,
        ``,
        `$url = ${JSON.stringify(url)};`,
        ``,
        `$context = stream_context_create([`,
        `    'http' => [`,
        `        'header' => 'Authorization: Bearer ' . getenv('${KEY_VARIABLE}'),`,
        `        // Read the body on an error status too; it carries the request id.`,
        `        'ignore_errors' => true,`,
        `    ],`,
        `]);`,
        ``,
        `$image = file_get_contents($url, false, $context);`,
        `if ($image === false) {`,
        `    throw new RuntimeException('The screenshot request failed.');`,
        `}`,
        ``,
        `file_put_contents('screenshot.${extension}', $image);`,
      ].join('\n');

    case 'python':
      return [
        `import os`,
        `import urllib.request`,
        ``,
        `url = ${JSON.stringify(url)}`,
        ``,
        `request = urllib.request.Request(`,
        `    url,`,
        `    headers={"Authorization": f"Bearer {os.environ['${KEY_VARIABLE}']}"},`,
        `)`,
        ``,
        `with urllib.request.urlopen(request) as response:`,
        `    with open("screenshot.${extension}", "wb") as file:`,
        `        file.write(response.read())`,
        `    print("request id:", response.headers.get("x-request-id"))`,
      ].join('\n');
  }
}
